// ExecutiveConversation — the always-available conversation entry + proactive
// messaging + learning actions + Executive Timeline. Faithful port of the approved
// artifact. Behavior is driven by the pure logic in lib/proactive.js; the demo
// events/answers are the isolated mock layer (mock/mockData.js). Every simulated
// value is clearly labeled placeholder in the UI.
//
// Three attention levels (per CEO decision):
//   Observation → slim indicator, light illumination, no interruption.
//   Suggestion  → compact card, illuminate, respond/remind/not-relevant.
//   Critical    → auto-expands the thread, stronger reasoning, interrupts.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ATTENTION, surfaceBehavior, statusLabel, pickScenario, recordSurface, applyLearning, recordAction } from "./lib/proactive";
import { MOCK_PROACTIVE_EVENTS, MOCK_CONVERSATION_SCENARIOS, MOCK_CONVERSATION_DEFAULT } from "./mock/mockData";

const LABELS = {
  cipher: "Cipher", meta: "Meta Intelligence", memory: "Memory", knowledge: "Knowledge", predictions: "Predictions",
  recommendations: "Recommendations", experiments: "Experiments", observations: "Observations", ghost: "Ghost Recon",
  google: "Google Intelligence", shopify: "Shopify", creative: "Creative Analysis", company: "Company Health", queue: "Executive Queue",
};
// Gates the fabricated-event demo control out of production builds.
const IS_PRODUCTION = process.env.NODE_ENV === "production";

const CipherGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 3c3.5 0 5 2 5 4.2 1.5.5 2 1.8 2 3.1 0 1-.5 2-1.4 2.5.2 2.4-1.7 4.2-4.1 4.2H8.6C6.2 17 4.3 15.2 4.5 12.8 3.6 12.3 3 11.3 3 10.3c0-1.3.5-2.6 2-3.1C5 5 6.5 3 12 3Z"/></svg>
);
const ClockGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
);

export default function ExecutiveConversation({ graph }) {
  const reduce = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion:reduce)").matches;
  const [messages, setMessages] = useState([]);
  const [threadOpen, setThreadOpen] = useState(false);
  const [threadCritical, setThreadCritical] = useState(false);
  const [threadState, setThreadState] = useState("Reasoning across the graph");
  const [proactive, setProactive] = useState(null); // { event, level, mode:'indicator'|'card' }
  const [alertRgb, setAlertRgb] = useState(null);
  const [micro, setMicro] = useState(null); // { text, tag }
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [placeholder, setPlaceholder] = useState("Ask Cipher anything about your business…");
  const inputRef = useRef(null);
  const idRef = useRef(0);
  const timersRef = useRef([]);
  const evIdxRef = useRef(0);

  const nid = () => "m" + ++idRef.current;
  const later = useCallback((fn, ms) => { const t = setTimeout(fn, reduce ? 0 : ms); timersRef.current.push(t); return t; }, [reduce]);
  const now = () => { try { return new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch (e) { return ""; } };

  const rest = useCallback(() => { if (graph.current) graph.current.rest(); }, [graph]);
  const clearSurfaces = useCallback(() => { setProactive(null); setThreadOpen(false); setThreadCritical(false); setMessages([]); setAlertRgb(null); }, []);

  const cipherMsg = (answer, path, followups) => ({ id: nid(), who: "c", html: answer, traced: path || [], followups: followups || null });
  const userMsg = (q) => ({ id: nid(), who: "u", html: q });
  const typingMsg = () => ({ id: nid(), who: "c", typing: true });

  const ask = useCallback((query, forcePath) => {
    let q = String(query || "").trim(); if (!q) q = "What should I focus on right now?";
    const sc = forcePath ? { path: forcePath, answer: MOCK_CONVERSATION_DEFAULT.answer, followups: MOCK_CONVERSATION_DEFAULT.followups }
      : pickScenario(q, MOCK_CONVERSATION_SCENARIOS, MOCK_CONVERSATION_DEFAULT);
    setProactive(null); setMicro(null); setAlertRgb(null); setThreadOpen(true);
    if (graph.current) graph.current.think(sc.path);
    setThreadState("Reasoning across the graph");
    const typing = typingMsg();
    setMessages((m) => [...m, userMsg(q), typing]);
    later(() => { setMessages((m) => m.filter((x) => x.id !== typing.id).concat(cipherMsg(sc.answer, sc.path, sc.followups)));
      setThreadState("Answered · still watching"); }, 820);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, later]);

  useEffect(() => { window.__ebAsk = ask; return () => { if (window.__ebAsk === ask) delete window.__ebAsk; }; }, [ask]);

  const showMicro = useCallback((text, tag) => { setMicro({ text, tag });
    later(() => setMicro(null), 3400); }, [later]);

  // ---- proactive surfacing: three distinct behaviors ----
  const openCritical = useCallback((ev) => {
    const L = ATTENTION[ev.level]; setAlertRgb(L.rgb);
    if (graph.current) graph.current.think(ev.involved, { strong: true, label: "Reasoning · urgent priority" });
    setThreadOpen(true); setThreadCritical(true); setThreadState("Urgent · needs your call");
    const typing = typingMsg();
    setMessages([cipherMsg(ev.msg, ev.involved, null), typing]);
    later(() => { setMessages((m) => m.filter((x) => x.id !== typing.id).concat(cipherMsg(ev.answer, ev.involved, ev.followups)));
      if (inputRef.current) inputRef.current.focus(); }, 700);
    setHistory((h) => recordAction(recordSurface(h, ev, now()), ev.id, "expanded"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, later]);

  const surface = useCallback((i) => {
    clearSurfaces(); setMicro(null);
    const ev = MOCK_PROACTIVE_EVENTS[i]; const L = ATTENTION[ev.level]; const b = surfaceBehavior(ev.level);
    evIdxRef.current = i;
    if (ev.level === "critical") { openCritical(ev); return; }
    setHistory((h) => recordSurface(h, ev, now()));
    if (b.surface === "indicator") { setProactive({ event: ev, level: ev.level, mode: "indicator" });
      if (graph.current) graph.current.glance(ev.involved); }
    else { setProactive({ event: ev, level: ev.level, mode: "card" });
      if (graph.current) graph.current.think(ev.involved); setAlertRgb(L.rgb); setPlaceholder("Respond to Cipher…"); }
  }, [clearSurfaces, graph, openCritical]);

  const respond = useCallback((ev) => {
    if (!ev) return;
    setHistory((h) => applyLearning(h, ev.id, "respond"));
    clearSurfaces(); setThreadOpen(true); if (ev.level === "critical") setThreadCritical(true);
    if (graph.current) graph.current.think(ev.involved, ev.level === "critical" ? { strong: true } : {});
    setThreadState("Awaiting your response");
    const typing = typingMsg();
    setMessages([cipherMsg(ev.msg, ev.involved, null), typing]);
    later(() => { setMessages((m) => m.filter((x) => x.id !== typing.id).concat(cipherMsg(ev.answer, ev.involved, ev.followups)));
      if (inputRef.current) inputRef.current.focus(); }, 700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSurfaces, graph, later]);

  const remindLater = useCallback((ev) => { if (!ev) return; setHistory((h) => applyLearning(h, ev.id, "remind_later"));
    clearSurfaces(); rest(); setPlaceholder("Ask Cipher anything about your business…");
    showMicro("Reminder set — Cipher will resurface this later.", "Temporary dismissal · retained in memory"); }, [clearSurfaces, rest, showMicro]);
  const notRelevant = useCallback((ev) => { if (!ev) return; setHistory((h) => applyLearning(h, ev.id, "not_relevant"));
    clearSurfaces(); rest(); setPlaceholder("Ask Cipher anything about your business…");
    showMicro("Marked not relevant — Cipher will learn from this feedback.", "Permanent feedback · still saved to your timeline"); }, [clearSurfaces, rest, showMicro]);

  const expandIndicator = useCallback(() => { const p = proactive; if (!p) return; const L = ATTENTION[p.level];
    setProactive({ event: p.event, level: p.level, mode: "card" });
    if (graph.current) graph.current.think(p.event.involved); setAlertRgb(L.rgb); setPlaceholder("Respond to Cipher…"); }, [proactive, graph]);

  const minimizeThread = useCallback(() => { setThreadOpen(false); setThreadCritical(false); rest(); setAlertRgb(null); }, [rest]);

  // Esc closes timeline → thread → proactive (in that order)
  useEffect(() => {
    const onKey = (e) => { if (e.key !== "Escape") return;
      if (timelineOpen) setTimelineOpen(false);
      else if (threadOpen) minimizeThread();
      else if (proactive) { setProactive(null); setAlertRgb(null); rest(); } };
    document.addEventListener("keydown", onKey); return () => document.removeEventListener("keydown", onKey);
  }, [timelineOpen, threadOpen, proactive, minimizeThread, rest]);

  // NOTE: proactive messages are NEVER surfaced automatically. Until Cipher is wired to
  // a real proactive source, every event in MOCK_PROACTIVE_EVENTS is fabricated, and a
  // fabricated claim about the business must never present itself to the founder
  // unprompted. Proactive content therefore renders only on an explicit trigger (the
  // dev-only demo control below, or a `respond` flow). Restoring any auto-surface timer
  // here would reintroduce that behavior — bind it to a real source instead.
  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  const submit = () => { const v = inputRef.current ? inputRef.current.value : ""; ask(v); if (inputRef.current) inputRef.current.value = ""; };
  const cycleDemo = () => { const next = (evIdxRef.current + 1) % MOCK_PROACTIVE_EVENTS.length; surface(next); };

  const ev = proactive && proactive.event; const L = ev ? ATTENTION[proactive.level] : null;
  const lvlStyle = L ? { "--lvl": "var(" + L.cssVar + ")", "--lvlrgb": L.rgb } : undefined;
  const alertStyle = alertRgb ? { "--albrgb": alertRgb } : undefined;

  const Traced = ({ path }) => (
    <div className="traced"><b>Reasoned through</b>{(path || []).map((id, i) => <i key={i}>{LABELS[id]}</i>)}</div>
  );

  return (
    <div className="ask-wrap" data-testid="executive-conversation">
      {/* Executive Timeline (permanent history) */}
      <div className={"timeline" + (timelineOpen ? " on" : "")}>
        <div className="tlhead"><span className="ic"><ClockGlyph /></span><b>Executive Timeline</b>
          <button className="cls" onClick={() => setTimelineOpen(false)}>Close</button></div>
        <div className="tlbody">
          {history.length === 0 ? (
            <div style={{ padding: "14px", color: "var(--dim)", fontSize: "12px" }}>No proactive events yet.</div>
          ) : history.map((r) => { const A = ATTENTION[r.level];
            return (
              <div className="tl-row" key={r.eid} style={{ "--lc": "var(" + A.cssVar + ")", "--lcr": A.rgb }}>
                <span className="tl-pill">{A.name}</span>
                <div className="tl-b"><div className="tl-msg" dangerouslySetInnerHTML={{ __html: r.msg }} />
                  <div className="tl-meta"><span className="tl-status">{statusLabel(r.status)}</span><span className="tl-time">{r.ts}</span></div></div>
              </div>
            ); })}
        </div>
        <div className="tlfoot">Every proactive event is retained in Cipher's memory — placeholder persistence. Nothing is lost when dismissed; these records will surface in Decisions, Memory &amp; the Executive Timeline once wired.</div>
      </div>

      {/* conversation thread */}
      <div className={"thread" + (threadOpen ? " on" : "") + (threadCritical ? " critical" : "")}>
        <div className="thead"><span className="sig"><CipherGlyph /></span><b>Cipher</b><span className="cpill">Critical</span>
          <span className="st"><span className="dot live" />{threadState}</span>
          <button className="min" onClick={minimizeThread}>Minimize</button></div>
        <div className="tbody">
          {messages.map((m) => m.who === "u" ? (
            <div className="msg u" key={m.id}><div className="who">NN</div><div className="bubble" dangerouslySetInnerHTML={{ __html: m.html }} /></div>
          ) : (
            <React.Fragment key={m.id}>
              <div className="msg c"><div className="who">◈</div>
                <div className="bubble">
                  {m.typing ? <div className="typing"><i /><i /><i /></div> : (
                    <>
                      <span dangerouslySetInnerHTML={{ __html: m.html }} />
                      <Traced path={m.traced} />
                      <div className="tag">Placeholder response · conversational engine not yet connected</div>
                    </>
                  )}
                </div>
              </div>
              {!m.typing && m.followups && m.followups.length > 0 && (
                <div className="followups">{m.followups.map((f, i) => <button className="fchip" key={i} onClick={() => ask(f)}>{f}</button>)}</div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* micro-confirmation after a learning action */}
      {micro && (
        <div className="micro on"><span className="mc"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6 9 17l-5-5"/></svg></span>
          <span>{micro.text}</span><span className="mtag">{micro.tag}</span></div>
      )}

      {/* SUGGESTION card */}
      {proactive && proactive.mode === "card" && (
        <div className="peek on" style={lvlStyle}>
          <div className="ph"><span className="glyph"><CipherGlyph /></span><span className="pt">{L.title}</span><span className="lvlpill">{L.name}</span></div>
          <p className="pm" dangerouslySetInnerHTML={{ __html: ev.msg }} />
          <div className="pacts">
            <button className="pbtn primary" onClick={() => respond(ev)}>Respond</button>
            <button className="pbtn ghost" onClick={() => remindLater(ev)}>Remind me later</button>
            <button className="pbtn ghost" onClick={() => notRelevant(ev)}>Not relevant</button>
            <span className="ptag">Placeholder event</span>
          </div>
        </div>
      )}

      {/* OBSERVATION indicator */}
      {proactive && proactive.mode === "indicator" && (
        <div className="glance-ind on" style={lvlStyle} onClick={expandIndicator}>
          <span className="gd" /><span className="glvl">{L.name}</span>
          <span className="gtx">{ev.msg}</span>
          <span className="ph-chip" data-testid="ph-observation">Placeholder</span>
          <span className="gex">Expand <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 15l6-6 6 6"/></svg></span>
        </div>
      )}

      {/* composer */}
      <div className={"askbar" + (alertRgb ? " alert" : "")} style={alertStyle} onClick={(e) => { if (!e.target.closest(".send")) inputRef.current && inputRef.current.focus(); }}>
        <span className="sig"><CipherGlyph /></span>
        <input ref={inputRef} placeholder={placeholder} onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />
        <div className="mic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3"/></svg></div>
        <div className="send" onClick={(e) => { e.stopPropagation(); submit(); }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12h15M13 6l6 6-6 6"/></svg></div>
      </div>

      <div className="history-bar">
        <button className="hlink" onClick={() => setTimelineOpen((o) => !o)}><ClockGlyph />Executive timeline · {history.length} event{history.length === 1 ? "" : "s"}</button>
        {/* Dev-only. The proactive events it cycles are fabricated, so this control must
            never reach production. CRA sets NODE_ENV=production for `craco build`, so this
            is compiled out of the production bundle entirely. */}
        {!IS_PRODUCTION && (
          <button className="demo-link" data-testid="demo-cycle" onClick={cycleDemo}>Demo: cycle attention level →</button>
        )}
      </div>
    </div>
  );
}
